/**
 * Thin wrapper around the AWS WAF CAPTCHA browser integration.
 *
 * Only public integration values are used here: the CAPTCHA integration URL
 * and the CAPTCHA API key are designed by AWS to be embedded in a public page
 * and are supplied at build time through environment variables. No secret or
 * confidential configuration is referenced by this module.
 */

const JSAPI_SCRIPT_ID = "aws-waf-captcha-jsapi";

// A solved CAPTCHA is refreshed before AWS WAF expires the token so the
// advisor is not asked to solve it again mid submission.
const DEFAULT_TOKEN_LIFETIME_SECONDS = 120;

let scriptPromise = null;

export function getCaptchaConfig() {
  return {
    jsApiUrl: process.env.VUE_APP_CAPTCHA_JSAPI_URL || "",
    apiKey: process.env.VUE_APP_CAPTCHA_API_KEY || "",
    tokenLifetimeSeconds:
      Number(process.env.VUE_APP_CAPTCHA_TOKEN_LIFETIME_SECONDS) ||
      DEFAULT_TOKEN_LIFETIME_SECONDS,
  };
}

export function isCaptchaConfigured() {
  const { jsApiUrl, apiKey } = getCaptchaConfig();
  // Guard against a placeholder or unresolved build value being injected into
  // a script tag, which would otherwise load the SPA's own index.html.
  return Boolean(
    apiKey && /^https:\/\/\S+\.js$/.test(jsApiUrl)
  );
}

/**
 * Loads the AWS WAF integration script once per page.
 */
export function loadCaptchaScript() {
  if (scriptPromise) {
    return scriptPromise;
  }

  if (!isCaptchaConfigured()) {
    return Promise.reject(new Error("CAPTCHA is not configured"));
  }

  const { jsApiUrl } = getCaptchaConfig();

  scriptPromise = new Promise((resolve, reject) => {
    const existing = document.getElementById(JSAPI_SCRIPT_ID);

    if (existing && window.AwsWafCaptcha) {
      resolve(window.AwsWafCaptcha);
      return;
    }

    const script = existing || document.createElement("script");
    script.id = JSAPI_SCRIPT_ID;
    script.src = jsApiUrl;
    script.defer = true;

    script.addEventListener("load", () => {
      if (window.AwsWafCaptcha) {
        resolve(window.AwsWafCaptcha);
      } else {
        scriptPromise = null;
        reject(new Error("CAPTCHA failed to initialise"));
      }
    });

    script.addEventListener("error", () => {
      scriptPromise = null;
      reject(new Error("CAPTCHA failed to load"));
    });

    if (!existing) {
      document.head.appendChild(script);
    }
  });

  return scriptPromise;
}

/**
 * Renders the CAPTCHA challenge into the supplied container element.
 */
export async function renderCaptcha(container, { onSuccess, onError, onLoad }) {
  const captcha = await loadCaptchaScript();
  const { apiKey } = getCaptchaConfig();

  captcha.renderCaptcha(container, {
    apiKey,
    onSuccess,
    onError,
    onLoad,
    skipTitle: false,
    dynamicWidth: true,
  });
}

/**
 * Returns the current AWS WAF token, if the integration has produced one.
 * The token is opaque to the application and is never persisted or logged.
 */
export async function getCaptchaToken() {
  if (!window.AwsWafIntegration) {
    return null;
  }

  try {
    return await window.AwsWafIntegration.getToken();
  } catch (error) {
    return null;
  }
}

/**
 * True when the browser still holds a usable WAF token.
 */
export function hasValidToken() {
  return Boolean(
    window.AwsWafIntegration &&
      typeof window.AwsWafIntegration.hasToken === "function" &&
      window.AwsWafIntegration.hasToken()
  );
}
